import React, { useEffect, useState } from "react";
import Header from "../../components/Header.jsx";
import { FaPlus, FaWallet } from "react-icons/fa";
import { GiPayMoney } from "react-icons/gi";
import { MdOutlineTrendingDown } from "react-icons/md";
import { IoCalendarOutline } from "react-icons/io5";
import AddExpenseModal from "./AddExpenseModal";
import Loading from "../../components/Loading/Loading";
import { useAuth } from "../../context/Auth.js";

const ExpenseList = () => {
  const { requestWithAuth } = useAuth();
  const [expenseList, setExpenseList] = useState([]);
  const [expenseTypes, setExpenseTypes] = useState([]);
  const [expenseModal, setExpenseModal] = useState(false);
  const [dataFetched, setDataFetched] = useState(false);
  const [expenseStats, setExpenseStats] = useState({
    total_expense: 0,
    monthly_expense: 0,
    expense_count: 0
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10); // Consistent page size with MemberManagement
  const [search, setSearch] = useState("");
  const [totalPages, setTotalPages] = useState(0);


  // GET EXPENSE LIST
  const getExpenseList = async (searchValue, currentPage, pageSize) => {
    try {
      const responseData = await requestWithAuth(`/finance/expense/?search=${searchValue}&pageSize=${pageSize}&currentPage=${currentPage}`, 'GET', {
        "Content-Type": "application/json"
      });
      setExpenseList(responseData?.results);
      setTotalPages(Math.ceil(responseData.count / pageSize)); 
      
      
    } catch (error) {
      console.log(error);
    }
  };

  const getExpenseData = async () => {
    try {
      const responseData = await requestWithAuth(`/finance/expense-data/`, 'GET', {
        "Content-Type": "application/json"
      });
      setExpenseStats(responseData);
    } catch (error) {
      console.log(error);
    }
  }

  // GET EXPENSE TYPES
  const getExpenseTypes = async () => {
    try {
      const responseData = await requestWithAuth(`/finance/expense-types/`, 'GET', {
        "Content-Type": "application/json"
      });
      setExpenseTypes(responseData);
    } catch (error) {
      console.log(error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return `GHS ${parseFloat(amount || 0).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  useEffect(() => {
    getExpenseList(search, currentPage, pageSize);
  }, [search, currentPage, pageSize]);

  useEffect(() => {
    const fetchData = async () => {
      await getExpenseTypes();
      await getExpenseData();
      setDataFetched(true);
    };
    fetchData();
  }, []);


  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  // Render page numbers for pagination (Copied from MemberManagement for consistency)
  const renderPageNumbers = () => {
    const pageNumbers = [];
    const totalNumbersToShow = 3; // Number of visible page buttons around current page

    let startPage = Math.max(
      1,
      currentPage - Math.floor(totalNumbersToShow / 2)
    );
    let endPage = Math.min(totalPages, startPage + totalNumbersToShow - 1);

    // Adjust start and end if at the boundaries
    if (endPage - startPage + 1 < totalNumbersToShow) {
      startPage = Math.max(1, endPage - totalNumbersToShow + 1);
    }

    if (startPage > 1) {
      pageNumbers.push(1);
      if (startPage > 2) {
        pageNumbers.push("...");
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pageNumbers.push("...");
      }
      pageNumbers.push(totalPages);
    }

    return pageNumbers.map((number, index) => (
      <button
        key={index}
        onClick={() => number !== "..." && handlePageClick(number)}
        disabled={number === "..."}
        className={`
                    min-w-[40px] h-10 flex items-center justify-center rounded-lg border text-sm font-medium transition-colors
                    ${
                      number === currentPage
                        ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                        : number === "..."
                        ? "bg-white text-gray-400 border-gray-200 cursor-default"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50 hover:border-gray-300"
                    }
                `}
      >
        {number}
      </button>
    ));
  };

  

  if (!dataFetched) {
    return <Loading />;
  }

  return (
    <div>
      {expenseModal && (
        <AddExpenseModal
          expenseTypes={expenseTypes}
          modal={expenseModal}
          setModal={setExpenseModal}
          getExpenseList={getExpenseList}
        />
      )}

      <Header title="EXPENSE MANAGEMENT" />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Expenses</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(expenseStats.total_expense)}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-red-500 flex items-center justify-center">
              <FaWallet className="text-white text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">This Month</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(expenseStats.monthly_expense)}
              </p>
            </div>
            <div className="h-12 w-12 rounded-full bg-orange-500 flex items-center justify-center">
              <MdOutlineTrendingDown className="text-white text-xl" />
            </div>
          </div>
        </div>

        <div className="bg-white shadow-md rounded-lg p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-500">Total Records</p>
              <p className="text-2xl font-bold text-gray-900">{expenseStats.expense_count}</p>
            </div>
            <div className="h-12 w-12 rounded-full bg-purple-500 flex items-center justify-center">
              <GiPayMoney className="text-white text-xl" />
            </div>
          </div>
        </div>
      </div>

      {/* Expense List */}
      <div className="bg-white shadow-md rounded-lg border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <h2 className="text-xl font-semibold text-gray-900">Expense Records</h2>
          <button
            onClick={() => setExpenseModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 transition-colors duration-200"
          >
            <FaPlus className="text-sm" />
            Add Expense
          </button>
        </div>

        <div className="overflow-x-auto">
          {expenseList.length > 0 ? (
            <div>
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expense ID
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Category
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Amount
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Payment Method
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {expenseList.map((expense, index) => (
                    <tr key={expense.id || index} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">
                          {expense.expense_id || 'N/A'}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                          {expense.expense_type?.name || expense.category || 'Other'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-red-600">
                          {formatCurrency(expense.amount_spent)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center text-sm text-gray-500">
                          <IoCalendarOutline className="mr-1" />
                          {formatDate(expense.date_recorded)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {expense.payment_method || expense.method || 'N/A'}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="px-6 py-4 border-t border-gray-200 bg-gray-50/30">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <p className="text-sm text-gray-700">
                    Showing page{" "}
                    <span className="font-medium">{currentPage}</span> of{" "}
                    <span className="font-medium">{totalPages}</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={handlePreviousPage}
                      disabled={currentPage === 1}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${
                                          currentPage === 1
                                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                    >
                      Previous
                    </button>

                    <div className="flex items-center gap-1">
                      {renderPageNumbers()}
                    </div>

                    <button
                      onClick={handleNextPage}
                      disabled={currentPage === totalPages}
                      className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors
                                        ${
                                          currentPage === totalPages
                                            ? "bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed"
                                            : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"
                                        }`}
                    >
                      Next
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <GiPayMoney className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No expense records found</h3>
              <p className="text-gray-500 mb-4">Get started by adding your first expense record.</p>
              <button
                onClick={() => setExpenseModal(true)}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center gap-2 mx-auto transition-colors duration-200"
              >
                <FaPlus className="text-sm" />
                Add First Expense
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ExpenseList;